import { analysisDisplayText } from '../utils/analysisDisplayText';
import Phase5Loading from './Phase5Loading';
import React, { useState, useEffect, useRef } from 'react';
import { Loader2, CheckCircle2, XCircle, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { PHASE5_REPORT_LABELS } from '../utils/phase5JobError';

/**
 * WhyNextProgressDialog
 * Modal shown while generating (or regenerating) the "Why" (Patterns &
 * Contributing Themes) or "Next Steps" reports. Displays live rotating
 * action ticks during generation, and a completion / error summary once done.
 *
 * Props:
 *   open (bool)             — controls modal visibility
 *   phase ('generating'|'done'|'error')
 *   kind  ('why'|'next')    — label switch
 *   isRegenerate (bool)     — controls "Regenerating…" wording
 *   errorMessage (string)   — shown when phase === 'error'
 *   onClose (fn)            — invoked when user dismisses the completed / error modal
 */
const WHY_TICKS = [
  'Loading raw Phase 1–4 client responses…',
  'Cross-referencing themes and repeated language…',
  'Grounding each pattern in exact client citations…',
  'Synthesizing contributing psychological factors…',
  'Applying anti-hallucination rules…',
  'Formatting the final Why Analysis…',
  'Almost done — proofreading output…',
];

const NEXT_TICKS = [
  'Loading the 14-Point SQIS + Patterns findings…',
  'Identifying actionable growth opportunities…',
  'Aligning next steps with client-specific evidence…',
  'Ordering recommendations by impact and feasibility…',
  'Applying anti-hallucination rules…',
  'Formatting the final Next Steps Analysis…',
  'Almost done — proofreading output…',
];

const SQIS_TICKS = [
  'Loading data from Phases 1–4…',
  'Building the Structured Qualitative Interpretative Summary…',
  'Extracting personality orientations & thematic domains…',
  'Cross-referencing themes and response valence…',
  'Grounding each point in exact client citations…',
  'Applying anti-hallucination rules…',
  'Formatting the 14 Points…',
  'Almost done — finalizing summary…',
];

const REPORT_TICKS = [
  'Loading the reviewed 14-Point SQIS + client feedback…',
  'Applying the admin-configured Analysis prompt…',
  'Analyzing client evidence across all phases…',
  'Composing the narrative with citations…',
  'Applying anti-hallucination rules…',
  'Formatting sections and headings…',
  'Almost done — proofreading output…',
];

const BOOKS_TICKS = [
  'Loading client psychological profile & themes…',
  'Matching titles to identified patterns and interests…',
  'Curating psychological-theme books for growth…',
  'Explaining specific fit for this client…',
  'Ranking and de-duplicating recommendations…',
  'Formatting the final book list…',
];

const MOVIES_TICKS = [
  'Loading client psychological profile & themes…',
  'Matching films to identified patterns and interests…',
  'Curating psychological-theme movies for growth…',
  'Explaining specific fit for this client…',
  'Ranking and de-duplicating recommendations…',
  'Formatting the final movie list…',
];

export const MUSIC_TICKS = [
  'Loading Why and Next Steps...',
  'Identifying themes suitable for reflective listening...',
  'Selecting appropriate music recommendations...',
  'Connecting each recommendation to the identified themes...',
  'Creating listening focus and reflection questions...',
  'Checking recommendations for safety and relevance...',
  'Formatting the final music recommendations...',
];

const CUSTOM_TICKS = [
  'Loading client data across all phases…',
  'Applying your custom focus and prompt…',
  'Composing the tailored narrative with citations…',
  'Applying anti-hallucination rules…',
  'Formatting the final custom analysis…',
];

const NEXUS_TICKS = [
  'Loading reviewed SQIS + Patterns + Next Steps…',
  'Mapping likely client objections & resistance points…',
  'Drafting MI-aligned reflections and reframes…',
  'Building the "Yeah, but…" objection matrix…',
  'Grounding every script in exact client evidence…',
  'Applying anti-hallucination rules…',
  'Formatting the Conversational Playbook…',
  'Almost done — proofreading output…',
];

export const NARRATIVE_PROBE_TICKS = [
  'Loading SQIS and supporting Analysis...',
  'Identifying important converging patterns...',
  'Detecting tensions and unresolved themes...',
  'Selecting priority exploration areas...',
  'Linking each area to supporting evidence...',
  'Generating Socratic questions...',
  'Formatting Narrative Probe...',
];

const WhyNextProgressDialog = ({
  open,
  phase,
  kind,
  isRegenerate = false,
  errorMessage = '',
  onClose,
  onCancel,               // optional — invoked while phase === 'generating'
  label: labelOverride,   // optional — takes precedence over kind
  ticks: ticksOverride,   // optional — takes precedence over kind
  attempts,               // optional — polled job attempts count (Phase 5 jobs only)
  maxAttempts,            // optional — admin-configured retry cap
  generatingTitle,
  generatingSubtitle,
  successTitle,
  successMessage,
}) => {
  const KIND_TO_TICKS = {
    why: WHY_TICKS,
    next: NEXT_TICKS,
    nexus: NEXUS_TICKS,
    narrative_probe: NARRATIVE_PROBE_TICKS,
    sqis: SQIS_TICKS,
    report: REPORT_TICKS,
    books: BOOKS_TICKS,
    movies: MOVIES_TICKS,
    music: MUSIC_TICKS,
    custom: CUSTOM_TICKS,
  };
  const KIND_TO_LABEL = {
    ...PHASE5_REPORT_LABELS,
    sqis: '14-Point SQIS',
    report: 'Analysis',
    books: 'Book Reflections',
    movies: 'Movie Reflections',
    music: 'Music Reflections',
    custom: 'Custom Analysis',
    narrative_probe: 'Narrative Probe',
  };
  const ticks = ticksOverride || KIND_TO_TICKS[kind] || WHY_TICKS;
  const label = labelOverride || KIND_TO_LABEL[kind] || 'Analysis';

  const [elapsed, setElapsed] = useState(0);
  const [tickIdx, setTickIdx] = useState(0);
  const [completedTicks, setCompletedTicks] = useState(0);
  const timerRef = useRef(null);
  const tickRef = useRef(null);

  useEffect(() => {
    if (open && phase === 'generating') {
      setElapsed(0);
      setTickIdx(0);
      setCompletedTicks(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
      tickRef.current = setInterval(() => {
        setTickIdx((i) => (i + 1) % ticks.length);
      }, 4500);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      if (tickRef.current) clearInterval(tickRef.current);
      if (phase === 'done') {
        // Mark every tick as completed on success
        setCompletedTicks(ticks.length);
      }
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [open, phase, ticks.length]);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const timeStr = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;

  const defaultTitle =
    phase === 'generating'
      ? `${isRegenerate ? 'Regenerating' : 'Generating'} ${label}…`
      : phase === 'done'
      ? `${label} ${isRegenerate ? 'Regenerated' : 'Generated'} Successfully`
      : `${label} Generation Failed`;
  const title = phase === 'generating' && generatingTitle
    ? generatingTitle
    : phase === 'done' && successTitle
      ? successTitle
      : defaultTitle;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && phase !== 'generating') onClose?.(); }}>
      <DialogContent
        className="max-w-lg"
        data-testid={`whynext-progress-dialog-${kind}`}
        onPointerDownOutside={(e) => { if (phase === 'generating') e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (phase === 'generating') e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {phase === 'generating' && (
              <>
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                <Sparkles className="w-4 h-4 text-indigo-400 animate-pulse" />
              </>
            )}
            {phase === 'done' && <CheckCircle2 className="w-5 h-5 text-green-600" />}
            {phase === 'error' && <XCircle className="w-5 h-5 text-red-600" />}
            <span>{analysisDisplayText(title)}</span>
          </DialogTitle>
          <DialogDescription>
            {phase === 'generating' && (
              <>
                {generatingSubtitle && <span className="block mb-2 text-sm text-gray-700">{generatingSubtitle}</span>}
                Elapsed: <span className="font-mono font-semibold">{timeStr}</span>
                <span className="block mt-1 text-xs italic text-gray-500">
                  Long Analysis can take 30–90 seconds. Closing this dialog only stops local status updates; the server job continues.
                </span>
                {attempts > 1 && maxAttempts && (
                  <span
                    className="inline-block mt-2 px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium"
                    data-testid={`whynext-retry-badge-${kind}`}
                  >
                    Attempt {attempts} of {maxAttempts} after restart…
                  </span>
                )}
              </>
            )}
            {phase === 'done' && analysisDisplayText(successMessage || `The ${label} Analysis is ready. You can now view or download it.`)}
            {phase === 'error' && analysisDisplayText(errorMessage || 'Something went wrong. Please try again.')}
          </DialogDescription>
        </DialogHeader>

        {phase === 'generating' && <Phase5Loading title={isRegenerate ? 'Regenerating Analysis' : 'Generating Analysis'} message="Waiting for the generated report to be saved…" />}
        <div className="mt-2 space-y-2 max-h-[320px] overflow-y-auto">
          {ticks.map((t, i) => {
            const isDone = phase === 'done' ? i < completedTicks : i < tickIdx;
            const isActive = phase === 'generating' && i === tickIdx;
            return (
              <div
                key={i}
                className={`flex items-start gap-2 text-sm transition-all duration-300 ${
                  isDone
                    ? 'text-green-700'
                    : isActive
                    ? 'text-blue-800 font-medium'
                    : 'text-gray-400'
                }`}
                data-testid={`whynext-tick-${kind}-${i}`}
              >
                {isDone ? (
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                ) : isActive ? (
                  <Loader2 className="w-4 h-4 mt-0.5 flex-shrink-0 animate-spin" />
                ) : (
                  <span className="w-4 h-4 mt-0.5 flex-shrink-0 rounded-full border border-gray-300" />
                )}
                <span className="leading-snug">{t}</span>
              </div>
            );
          })}
        </div>

        {phase === 'generating' && onCancel && (
          <div className="flex justify-end pt-2">
            <Button
              onClick={onCancel}
              variant="outline"
              className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
              data-testid={`whynext-cancel-btn-${kind}`}
            >
              Stop Watching
            </Button>
          </div>
        )}

        {(phase === 'done' || phase === 'error') && (
          <div className="flex justify-end pt-2">
            <Button
              onClick={onClose}
              variant={phase === 'error' ? 'destructive' : 'default'}
              data-testid={`whynext-close-btn-${kind}`}
            >
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WhyNextProgressDialog;
